import React, { useState, useEffect, useCallback } from "react";
import { Alert, Col, Form, Row, Table } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableFooter from "../../../components/TableFooter.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";
import { formatDirection } from "../directions.js";
import {
  selectIsLoadingTrainStopSignalImages,
  selectTrainStopSignalImages,
} from "../selectors.js";
import { fetchTrainStopSignalImages } from "../thunks.js";

import SearchDropdownForm from "./SearchDropdownForm.jsx";

const messages = {
  searchByCode: {
    id: "modules.TrainStopSignalImages.components.SignalImagesList.searchByCode",
    description: "Search by code placeholder",
    defaultMessage: "Search by code...",
  },
  stationName: {
    id: "modules.TrainStopSignalImages.components.SignalImagesList.stationName",
    description: "stationName label",
    defaultMessage: "Station name",
  },
  signalImageRequired: {
    id: "modules.TrainStopSignalImages.components.SignalImagesList.signalImageRequired",
    description: "signalImageRequired label",
    defaultMessage: "Signal image required",
  },
  direction: {
    id: "modules.TrainStopSignalImages.components.SignalImagesList.direction",
    description: "Direction column label",
    defaultMessage: "Direction",
  },
  allDirections: {
    id: "modules.TrainStopSignalImages.components.SignalImagesList.allDirections",
    description: "Signal image required in all directions",
    defaultMessage: "All directions",
  },
  towards: {
    id: "modules.TrainStopSignalImages.components.SignalImagesList.towards",
    description: "Signal image only required towards the given stations",
    defaultMessage: "Towards {stations}",
  },
  searchExistingSignalImages: {
    id: "modules.TrainStopSignalImages.components.SignalImagesList.searchExistingStations",
    description: "searchExistingStations label",
    defaultMessage: "Search existing signal images...",
  },
};

const SignalImages = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const pageSize = 15;

  const trainStopSignalImages = useSelector(selectTrainStopSignalImages);
  const isLoading = useSelector(selectIsLoadingTrainStopSignalImages);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(async () => {
    try {
      const pagesFromResponse = await dispatch(
        fetchTrainStopSignalImages({ page, pageSize, search: debouncedSearch }),
      );
      setPages(pagesFromResponse);
    } catch (err) {
      setError(err);
      setPage(1);
      setPages(0);
    }
  }, [dispatch, page, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSelectPage = (newPage) => setPage(newPage);

  const dismissError = () => setError(null);

  const formatDirections = (directions) =>
    directions?.length
      ? intl.formatMessage(messages.towards, {
          stations: intl.formatList(directions.map(formatDirection), {
            type: "disjunction",
          }),
        })
      : intl.formatMessage(messages.allDirections);

  const mapStopToTableRow = (stop) => (
    <tr key={`stop-${stop.stopCode}`}>
      <td>
        <Link to={`/signal-image/edit/${stop.stopCode}`}>
          {stop.stationName}
        </Link>
      </td>
      <td>
        {intl.formatMessage(
          stop.signalImageRequired
            ? genericTranslations.yes
            : genericTranslations.no,
        )}
      </td>
      <td>{formatDirections(stop.directions)}</td>
    </tr>
  );

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={dismissError} dismissible>
          <Alert.Heading>
            {intl.formatMessage(genericTranslations.errorMessage)}
          </Alert.Heading>
          <p>{error.toString()}</p>
        </Alert>
      )}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder={intl.formatMessage(
              messages.searchExistingSignalImages,
            )}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>

        <Col md={6}>
          <SearchDropdownForm setError={setError} />
        </Col>
      </Row>

      {isLoading && (
        <span className="align-middle">
          {intl.formatMessage(genericTranslations.loadingInProgress)}
        </span>
      )}

      {!isLoading && trainStopSignalImages.length === 0 && (
        <span className="align-middle">
          {intl.formatMessage(genericTranslations.nothingFound)}
        </span>
      )}

      {trainStopSignalImages.length > 0 && !isLoading && (
        <Table responsive>
          <thead>
            <tr>
              <td>{intl.formatMessage(messages.stationName)}</td>
              <td>{intl.formatMessage(messages.signalImageRequired)}</td>
              <td>{intl.formatMessage(messages.direction)}</td>
            </tr>
          </thead>
          <tbody>{trainStopSignalImages.map(mapStopToTableRow)}</tbody>
        </Table>
      )}

      <TableFooter onSelectPage={onSelectPage} page={page} pages={pages} />
    </div>
  );
};

export default SignalImages;
